const { atob, btoa, decodeURIComponent, encodeURIComponent } = window;
import { getEngageHostnameByEnv } from '@pendo/services/Environments';
import { getActiveSubscription } from '@/data/session/session-globals';
import { appLoadingSpinnerStyle, appLoadingSpinnerMarkup } from '@utils/app-loading-spinner.mjs';
const DESIGNER_URL_STORAGE_BASE_KEY = 'guide.designer.urls';
const VDS_BUCKET = 'in-app-designer';

export const DESIGNER_LAUNCH_TOKEN_LS_KEY = 'pendo-designer-launch-token';

// eslint-disable-next-line internal-rules/no-hardcoded-colors
export const LOCAL_STORAGE_WHITE_LABEL_KEY = 'adopt-white-label-token';

export function generateDesignerToken (config) {
    return encodeURIComponent(btoa(JSON.stringify(config)));
}

export function parseDesignerToken (token) {
    if (!token) {
        return {};
    }

    return JSON.parse(atob(decodeURIComponent(token)));
}

function stripDesignerToken (url) {
    if (!url.includes('pendo-designer=')) {
        return url;
    }

    const [base, hash = ''] = url.split('#');
    const queryIndex = base.indexOf('?');
    if (queryIndex === -1) {
        return url;
    }

    const path = base.slice(0, queryIndex);
    const params = base
        .slice(queryIndex + 1)
        .split('&')
        .filter((param) => param && !param.startsWith('pendo-designer='));
    const rebuiltBase = params.length ? `${path}?${params.join('&')}` : path;

    return hash ? `${rebuiltBase}#${hash}` : rebuiltBase;
}

const DESIGNER_LAUNCH_OVERLAY_ID = 'designer-launch-app-overlay';

/* eslint-disable internal-rules/no-hardcoded-colors */
export function renderDesignerLaunchLoader (launchWindow) {
    if (!launchWindow) {
        return;
    }

    const theme = document.documentElement.getAttribute('data-theme') || '';
    const product = document.documentElement.getAttribute('data-product') || 'pendo';
    const loaderHtml = `<!DOCTYPE html><html data-theme="${theme}" data-product="${product}"><head><meta charset="utf-8"><style>
        html,body{height:100%;margin:0}
        body{display:flex;align-items:center;justify-content:center;background:#FBFAF8}
        html[data-theme="dark"] body{background:#191414}
        ${appLoadingSpinnerStyle}
    </style></head><body>${appLoadingSpinnerMarkup}</body></html>`;

    launchWindow.document.write(loaderHtml);
    launchWindow.document.close();
}

export function showDesignerLaunchOverlay () {
    if (typeof document === 'undefined' || document.getElementById(DESIGNER_LAUNCH_OVERLAY_ID)) {
        return;
    }

    const overlay = document.createElement('div');
    overlay.id = DESIGNER_LAUNCH_OVERLAY_ID;
    overlay.innerHTML = `<style>
        #${DESIGNER_LAUNCH_OVERLAY_ID}{position:fixed;top:0;left:0;width:100vw;height:100vh;display:flex;align-items:center;justify-content:center;background:#FBFAF8;z-index:2147483000}
        [data-theme="dark"] #${DESIGNER_LAUNCH_OVERLAY_ID}{background:#191414}
        ${appLoadingSpinnerStyle}
    </style>${appLoadingSpinnerMarkup}`;
    document.body.appendChild(overlay);
}

export function hideDesignerLaunchOverlay () {
    if (typeof document === 'undefined') {
        return;
    }

    document.getElementById(DESIGNER_LAUNCH_OVERLAY_ID)?.remove();
}
/* eslint-enable internal-rules/no-hardcoded-colors */

export function getDesignerOpenerUrl (appUrl, config) {
    const token = generateDesignerToken(config);
    const baseUrl = stripDesignerToken(appUrl);

    const urlHasQuery = baseUrl.includes('?');
    let next = baseUrl;

    if (urlHasQuery) {
        next += `&pendo-designer=${token}`;
    } else {
        next += `?pendo-designer=${token}`;
    }

    return next;
}

export function getDesignerLocationKeyForApp (appId = '-323232') {
    return `${DESIGNER_URL_STORAGE_BASE_KEY}.${getActiveSubscription()?.id}.${appId}`;
}

export function clearAllDesignerLocations (storage) {
    return Promise.all(
        [getDesignerLocationKeyForApp(), DESIGNER_URL_STORAGE_BASE_KEY].map((key) => storage.set(key, '')),
    );
}

export function getStaticPathPattern () {
    return new RegExp(/\/in-app-designer+\/([^]*)\/designer.html/);
}

export function isStaticDesignerPath (url = '') {
    const staticPathRegex = getStaticPathPattern();

    return staticPathRegex.test(url);
}

// When running the local dev server, `origin` (local.pendo.io) isn't a real backend, so
// `localEnv` (the --env passed to `npm run start`) is used to resolve the actual host instead.
export function resolveDesignerHost (localEnv, origin) {
    if (!localEnv) {
        return origin;
    }

    return appendHttpsToUrl(getEngageHostnameByEnv(localEnv));
}

export function appendHttpsToUrl (url) {
    if (!url.startsWith('https://') && !url.startsWith('http://')) {
        return `https://${url}`;
    }

    return url;
}

export function convertToStaticLookasideConfigIfEligible (config = {}) {
    if (isStaticDesignerPath(config.host)) {
        config = {
            ...config,
            ...generateLaunchParamsForStaticDeploy(config.host),
        };
    }

    return config;
}

export function generateVDSLaunchParams (url) {
    return generateLaunchParamsForStaticDeploy(url, VDS_BUCKET);
}

export function generateLaunchParamsForStaticDeploy (url, gcsBucket = VDS_BUCKET) {
    if (!url) {
        return { gcsBucket };
    }

    const buildUrl = new URL(appendHttpsToUrl(url));

    const { pathname, host } = buildUrl;

    const [, gcsBucketOverride, target] = pathname.split('/');

    return {
        gcsBucket: gcsBucketOverride || gcsBucket,
        target,
        host: appendHttpsToUrl(host),
    };
}
